import React from 'react'
import { Button, ButtonProps } from 'antd'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import BarsOutlined from '@ant-design/icons/BarsOutlined'
import PlusSquareOutlined from '@ant-design/icons/PlusSquareOutlined'
import RedoOutlined from '@ant-design/icons/RedoOutlined'
import SaveOutlined from '@ant-design/icons/SaveOutlined'

type HideText = { hideText?: boolean }

type LegacyResourceProps = {
  resource?: string
  resourceNameOrRouteName?: string
}

const resolveResource = (
  resource: string | undefined,
  resourceNameOrRouteName: string | undefined,
): string | undefined => resource ?? resourceNameOrRouteName

export type RefreshButtonProps = ButtonProps & HideText

export const RefreshButton: React.FC<RefreshButtonProps> = ({
  hideText,
  children,
  ...rest
}) => {
  const { t: translate } = useTranslation()
  return (
    <Button icon={<RedoOutlined />} {...rest}>
      {hideText ? null : children ?? translate('buttons.refresh', 'Refresh')}
    </Button>
  )
}

export type ListButtonProps = ButtonProps & HideText & LegacyResourceProps

export const ListButton: React.FC<ListButtonProps> = ({
  onClick,
  resource,
  resourceNameOrRouteName,
  hideText,
  children,
  ...rest
}) => {
  const navigate = useNavigate()
  const { t: translate } = useTranslation()
  const target = resolveResource(resource, resourceNameOrRouteName)

  const handleClick: React.MouseEventHandler<HTMLElement> = (e) => {
    if (onClick) {
      onClick(e)
      return
    }
    if (target) {
      navigate(`/${target}`)
    }
  }

  return (
    <Button icon={<BarsOutlined />} onClick={handleClick} {...rest}>
      {hideText ? null : children ?? translate('buttons.list', 'List')}
    </Button>
  )
}

export type SaveButtonProps = ButtonProps & HideText

export const SaveButton: React.FC<SaveButtonProps> = ({
  hideText,
  children,
  ...rest
}) => {
  const { t: translate } = useTranslation()
  return (
    <Button
      icon={<SaveOutlined />}
      type="primary"
      htmlType="submit"
      {...rest}
    >
      {hideText ? null : children ?? translate('buttons.save', 'Save')}
    </Button>
  )
}

export type CreateButtonProps = ButtonProps & HideText & LegacyResourceProps

export const CreateButton: React.FC<CreateButtonProps> = ({
  onClick,
  resource,
  resourceNameOrRouteName,
  hideText,
  children,
  ...rest
}) => {
  const navigate = useNavigate()
  const { t: translate } = useTranslation()
  const target = resolveResource(resource, resourceNameOrRouteName)

  const handleClick: React.MouseEventHandler<HTMLElement> = (e) => {
    if (onClick) {
      onClick(e)
      return
    }
    if (target) {
      navigate(`/${target}/create`)
    }
  }

  return (
    <Button
      icon={<PlusSquareOutlined />}
      type="primary"
      onClick={handleClick}
      {...rest}
    >
      {hideText ? null : children ?? translate('buttons.create', 'Create')}
    </Button>
  )
}
